import { Component } from '@theme/component'
import { morph } from '@theme/morph'
import { isClickedOutside, useReduced } from '@theme/utils'

const CollectionConfig = {
  infinite: {
    attributes: {
      busy: 'aria-busy'
    },
    modes: {
      click: 'click',
      infinite: 'infinite'
    },
    rootMargin: '200px 0px 200px 0px',
    selectors: {
      grid: '#ProductGridContainer',
      pagination: '.pagination'
    }
  },
  sort: {
    selectors: {
      button: '.sort-by',
      close: '.sort-close',
      radio: 'input[type="radio"][name="sort_by"]',
      selection: '.sort-selection'
    }
  }
}

class FacetSort extends Component {
  connectedCallback() {
    super.connectedCallback()

    this.button = this.querySelector(CollectionConfig.sort.selectors.button)
    this.selection = this.querySelector(
      CollectionConfig.sort.selectors.selection
    )
    this.closeBtn = this.querySelector(CollectionConfig.sort.selectors.close)

    if (!(this.button && this.selection)) return

    this.handleButtonClick = this.handleButtonClick.bind(this)
    this.handleCloseClick = this.handleCloseClick.bind(this)
    this.handleChange = this.handleChange.bind(this)
    this.handleOutsideClick = this.handleOutsideClick.bind(this)

    this.button.addEventListener('click', this.handleButtonClick)
    this.closeBtn?.addEventListener('click', this.handleCloseClick)
    this.addEventListener('change', this.handleChange)
    document.addEventListener('click', this.handleOutsideClick, true)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.button?.removeEventListener('click', this.handleButtonClick)
    this.closeBtn?.removeEventListener('click', this.handleCloseClick)
    this.removeEventListener('change', this.handleChange)
    document.removeEventListener('click', this.handleOutsideClick, true)
  }

  get isOpen() {
    return this.button?.hasAttribute('open')
  }

  open() {
    this.button?.setAttribute('open', '')
  }

  close() {
    this.button?.removeAttribute('open')
  }

  handleButtonClick(event) {
    event.preventDefault()
    this.isOpen ? this.close() : this.open()
  }

  handleCloseClick(event) {
    event.preventDefault()
    event.stopPropagation()
    this.close()
  }

  handleOutsideClick(event) {
    if (this.isOpen && isClickedOutside(event, this)) {
      this.close()
    }
  }

  handleChange(event) {
    const target = event.target
    if (!(target instanceof HTMLInputElement) || target.name !== 'sort_by') {
      return
    }

    const label = target.nextElementSibling?.textContent?.trim()
    if (label) this.selection.textContent = label

    const url = new URL(window.location.href)
    url.searchParams.set('sort_by', target.value)
    url.searchParams.delete('page')
    window.location.assign(url.toString())
  }
}

class InfiniteButton extends HTMLButtonElement {
  #abortController = null
  #intersectionObserver = null

  connectedCallback() {
    this.handleClick = this.handleClick.bind(this)
    this.addEventListener('click', this.handleClick)

    if (
      this.getAttribute('mode') === CollectionConfig.infinite.modes.infinite &&
      !useReduced()
    ) {
      this.#observeForInfinite()
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick)
    this.#abortController?.abort()
    this.#intersectionObserver?.disconnect()
  }

  #observeForInfinite() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !this.isBusy) {
          this.handleClick()
        }
      },
      { rootMargin: CollectionConfig.infinite.rootMargin }
    )
    this.#intersectionObserver.observe(this)
  }

  get isBusy() {
    return this.hasAttribute(CollectionConfig.infinite.attributes.busy)
  }

  setBusy(isBusy) {
    if (isBusy) {
      this.setAttribute(CollectionConfig.infinite.attributes.busy, 'true')
      this.classList.add('pointer-events-none')
    } else {
      this.removeAttribute(CollectionConfig.infinite.attributes.busy)
      this.classList.remove('pointer-events-none')
    }
  }

  async handleClick(event) {
    event?.preventDefault?.()
    if (this.isBusy) return

    const action = this.getAttribute('action')
    if (!action) return

    this.#abortController?.abort()
    this.#abortController = new AbortController()
    this.setBusy(true)

    try {
      const response = await fetch(action, {
        signal: this.#abortController.signal
      })
      const text = await response.text()
      this.#applyResponse(text)
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.setBusy(false)
      }
    }
  }

  #applyResponse(text) {
    const parsed = new DOMParser().parseFromString(text, 'text/html')
    const currentGrid = document.querySelector(
      CollectionConfig.infinite.selectors.grid
    )
    const incomingGrid = parsed.querySelector(
      CollectionConfig.infinite.selectors.grid
    )
    if (!(currentGrid && incomingGrid)) {
      this.setBusy(false)
      return
    }

    const currentPagination = currentGrid.querySelector(
      CollectionConfig.infinite.selectors.pagination
    )
    const incomingPagination = incomingGrid.querySelector(
      CollectionConfig.infinite.selectors.pagination
    )

    const currentList = currentGrid.querySelector('motion-list, .card-grid')
    const incomingList = incomingGrid.querySelector('motion-list, .card-grid')

    if (currentList && incomingList) {
      for (const card of Array.from(incomingList.children)) {
        currentList.appendChild(card)
      }
    }

    if (currentPagination && incomingPagination) {
      morph(currentPagination, incomingPagination)
    } else {
      currentPagination?.remove()
    }
  }
}

if (!customElements.get('facet-sort')) {
  customElements.define('facet-sort', FacetSort)
}

if (!customElements.get('infinite-button')) {
  customElements.define('infinite-button', InfiniteButton, {
    extends: 'button'
  })
}
